import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, LogIn, Settings, LayoutDashboard, Bookmark, Loader2 } from "lucide-react";
import SEO from "../components/seo/SEO";
import { useAuth } from "../context/auth";
import { getCalculator } from "../config/calculators";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, openAuthModal, loading: authLoading } = useAuth();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    if (!user?.plus) { setLoading(false); return; }
    axios.get(`${API}/scenarios`, { withCredentials: true })
      .then((r) => setScenarios(r.data.scenarios || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await axios.post(`${API}/billing/portal`, { lookup_key: "", origin_url: window.location.origin }, { withCredentials: true });
      window.location.href = data.url;
    } catch {
      setPortalBusy(false);
      toast.error("Couldn't open the billing portal.");
    }
  };

  if (authLoading) return <div className="py-24 text-center"><Loader2 className="mx-auto h-8 w-8 animate-spin text-brand" /></div>;

  if (!user) return (
    <Gate title="Sign in to see your dashboard" body="Your personal money dashboard rolls up everything you've saved across our tools.">
      <button onClick={() => openAuthModal()} data-testid="dashboard-signin" className="inline-flex items-center gap-2 rounded-full bg-action px-6 py-3 text-sm font-semibold text-white hover:bg-action-hover"><LogIn className="h-4 w-4" /> Sign in</button>
    </Gate>
  );

  if (!user.plus) return (
    <Gate title="Your dashboard is a Guru Plus feature" body="Upgrade to see net worth, retirement and debt progress from all your saved scenarios in one place.">
      <Link to="/guru-plus" data-testid="dashboard-upgrade" className="inline-flex items-center gap-2 rounded-full bg-action px-6 py-3 text-sm font-semibold text-white hover:bg-action-hover"><Sparkles className="h-4 w-4" /> Explore Guru Plus</Link>
    </Gate>
  );

  const byCalc = scenarios.reduce((acc, s) => { (acc[s.calculator] = acc[s.calculator] || []).push(s); return acc; }, {});

  return (
    <>
      <SEO title="Your Dashboard — MyFinancesGuru Tools" description="Your personal money dashboard." path="/dashboard" noindex />
      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 font-heading text-3xl font-bold text-foreground"><LayoutDashboard className="h-7 w-7 text-brand" /> Your dashboard</h1>
            <p className="mt-1 text-muted-foreground">Welcome back, {user.name || user.email}. <span className="font-semibold text-brand">Guru Plus</span> member.</p>
          </div>
          <button onClick={openPortal} disabled={portalBusy} data-testid="manage-subscription" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-secondary">
            {portalBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings className="h-4 w-4" />} Manage subscription
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Saved scenarios" value={scenarios.length} />
          <Stat label="Tools used" value={Object.keys(byCalc).length} />
          <Stat label="Plan" value="Guru Plus" />
          <Stat label="Status" value="Active" />
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="mx-auto h-8 w-8 animate-spin text-brand" /></div>
        ) : scenarios.length === 0 ? (
          <div className="mt-10 rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="dashboard-empty">
            No saved scenarios yet. Open any <Link to="/calculators" className="font-semibold text-brand">calculator</Link> and save a scenario — it'll show up here.
          </div>
        ) : (
          <div className="mt-10 space-y-8" data-testid="dashboard-rollup">
            {Object.entries(byCalc).map(([slug, list]) => {
              const calc = getCalculator(slug);
              return (
                <div key={slug}>
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading text-lg font-semibold text-foreground">{calc?.name || slug}</h2>
                    {calc && <Link to={`/calculators/${slug}`} className="text-sm font-semibold text-brand hover:text-action">Open tool →</Link>}
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {list.map((s) => (
                      <div key={s.id} className="rounded-xl border border-border bg-card p-4">
                        <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><Bookmark className="h-4 w-4 text-brand" /> {s.name}</div>
                        <p className="mt-1 text-xs text-muted-foreground">Saved {(s.created_at || "").slice(0, 10)}</p>
                        <p className="mt-2 text-xs text-muted-foreground">{Object.keys(s.values || {}).length} inputs saved</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 font-heading text-2xl font-bold text-foreground">{value}</p>
    </div>
  );
}

function Gate({ title, body, children }) {
  return (
    <>
      <SEO title="Dashboard — MyFinancesGuru Tools" description="Your personal money dashboard." path="/dashboard" noindex />
      <div className="mx-auto max-w-lg px-4 py-24 text-center">
        <Sparkles className="mx-auto h-10 w-10 text-brand" />
        <h1 className="mt-4 font-heading text-3xl font-bold text-foreground">{title}</h1>
        <p className="mt-3 text-muted-foreground">{body}</p>
        <div className="mt-6">{children}</div>
      </div>
    </>
  );
}
